import { useQuery } from 'react-query'

import { handleException } from 'clients/exception-handling'
import type { YogiQLChartsService } from 'features/charts-v2/service/yogiql-service'
import type { FieldCatalogResponse } from 'features/charts-v2/types/yogiql'
import {
  errorMessage,
  warnYogiQLFallback,
} from 'features/charts-v2/utils/warn-yogiql-fallback'

/** The field catalog's query key; a reader of the cache uses the same. */
export const yogiqlFieldCatalogKey = (
  projectId: string | null,
): [string, string | null] => ['charts-v2-yogiql-field-catalog', projectId]

/**
 * The hub's field catalog, fetched when `enabled`; undefined until it loads.
 * One query key for every caller, so a page that needs it twice (its own
 * YogiQL route, and what it tells Ask Yogi) fetches it once. Not gated on any
 * flag: each caller says when it needs the catalog.
 *
 * @param fallback - Said when the request fails: what the caller does instead.
 */
export const useFieldCatalog = (
  service: YogiQLChartsService,
  projectId: string | null,
  enabled: boolean,
  fallback: string,
): { catalog: FieldCatalogResponse | undefined; settled: boolean } => {
  // A transient outage heals: retry, and an errored query refetches on the
  // next mount instead of sticking. onError fires once per failure.
  const { data: catalog, status } = useQuery(
    yogiqlFieldCatalogKey(projectId),
    () => service.getFieldCatalog(projectId!),
    {
      meta: { operation: 'chart.field-catalog.read' },
      enabled: enabled && !!projectId,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: 2,
      retryOnMount: true,
      // react-query notifies every observer of a failure, a disabled one
      // too; only a caller that asked says what it does instead.
      onError: (error) => {
        handleException(error, {
          operation: 'chart.field-catalog.read',
          disposition: 'delegated',
          owner: 'QueryCache',
        })

        if (enabled) {
          warnYogiQLFallback(
            `field-catalog request failed, ${fallback}: ${errorMessage(error)}`,
          )
        }
      },
    },
  )
  return { catalog, settled: status !== 'loading' }
}
